import { createContext, useContext, useState } from "react";

export const LANGUAGES = [
  { code: "en", label: "English",  langWord: "Language" },
  { code: "es", label: "Español",  langWord: "Idioma"   },
  { code: "zh", label: "中文",      langWord: "语言"      },
  { code: "fr", label: "Français", langWord: "Langue"   },
];

export const translations = {
  en: {
    nav: {
      home: "Home", voiceAnalysis: "Voice Analysis",
      skinAnalysis: "Skin Analysis", dashboard: "Dashboard",
    },
    home: {
      badge: "AI-Powered Health Screening",
      headline1: "Understand your health",
      headline2: "before the doctor visit.",
      subtitle: "WorryLens uses AI to screen your voice and skin for potential health signals — giving you clarity and context, not diagnoses.",
      analyzeVoice: "Analyze Voice", analyzeSkin: "Analyze Skin",
      whatCanScreen: "What can WorryLens screen?",
      uploadSubtitle: "Upload a file or record directly — results in seconds.",
      voiceBadge: "Live recording", voiceCardTitle: "Voice Analysis",
      voiceCardDesc: "Upload an audio file or record in the browser. Our AI extracts pitch, energy, and vocal patterns to identify signs of strain, fatigue, or possible respiratory issues.",
      skinBadge: "AI Vision", skinCardTitle: "Skin Analysis",
      skinCardDesc: "Upload a photo of a skin concern. Our AI identifies potential conditions like acne, rashes, or mole irregularities and asks follow-up questions for a refined assessment.",
      tryItNow: "Try it now",
      howItWorks: "How it works",
      step1Title: "Upload or Record", step1Desc: "Drag and drop a file or use your microphone/camera directly in the browser. No account needed.",
      step2Title: "AI Analysis",     step2Desc: "Our system extracts audio/visual features, runs rule-based screening, then generates a plain-language explanation.",
      step3Title: "Clear Results",   step3Desc: "See your risk level, key findings, and actionable health tips — all saved to your personal dashboard.",
      privateTitle: "Private by default", privateDesc: "Files are analyzed in memory and never stored on our servers.",
      aiTitle: "Powered by WorryLens",    aiDesc: "Explanations are generated from our health database — accurate and nuanced.",
      transparentTitle: "Transparent results", transparentDesc: "We show you raw feature data alongside every AI explanation.",
    },
    voice: {
      title: "Voice Analysis", subtitle: "Detect signs of vocal strain, fatigue, or respiratory issues",
      uploadTitle: "Upload or Record Audio",
      dragDrop: "Drag & drop an audio file", dropHere: "Drop your audio file here",
      fileTypes: "WAV, MP3, M4A — or click to browse",
      recordMic: "Record from microphone", recording: "Recording",
      stopAnalyze: "Stop & Analyze", analyze: "Analyze",
      loading: "Extracting vocal features and generating explanation…",
      errorFallback: "Analysis failed. Is the backend running on port 8001?",
      result: "Result", file: "File", confidence: "Confidence",
      anomalyFlags: "Anomaly flags", extractedFeatures: "Extracted Features",
      pitchMean: "Pitch Mean", pitchVariability: "Pitch Variability",
      vocalEnergy: "Vocal Energy", mfccMean: "MFCC Mean",
      zeroCrossing: "Zero Crossing Rate", spectralCentroid: "Spectral Centroid",
      aiExplanation: "AI Explanation", poweredBy: "Powered by WorryLens",
      micDenied: "Microphone access denied. Please allow microphone access in your browser settings.",
      error: "Error",
    },
    skin: {
      title: "Skin Analysis", subtitle: "AI-powered screening for acne, rashes, moles, and more",
      uploadTitle: "Upload Skin Photo",
      dragDrop: "Drag & drop a skin photo", dropHere: "Drop your image here",
      fileTypes: "JPG, PNG, WEBP — or click to browse",
      analyzeImage: "Analyze Image",
      loading: "WorryLens is thinking…",
      errorFallback: "Analysis failed. Is the backend running on port 8001?",
      riskAssessment: "Risk Assessment", confidenceLabel: "Confidence",
      possibleConditions: "Possible Conditions",
      aiObservations: "AI Observations", poweredByVision: "Powered by Database Images",
      recommendations: "Recommendations",
      followUpTitle: "Follow-up Questions", followUpSub: "Refines the assessment",
      followUpPlaceholder: "Your answer…",
      getRefined: "Get Refined Assessment", refining: "Refining…",
      refinedAssessment: "Refined Assessment", refinedConditions: "Refined Conditions",
      actionPlan: "Action Plan", urgency: "Urgency:",
      followUpFailed: "Follow-up analysis failed.", removeImage: "Remove image",
      error: "Error",
    },
    dashboard: {
      title: "Dashboard", subtitle: "Your recent analyses — stored locally in this browser",
      clearAll: "Clear all", totalScans: "Total Scans",
      voiceScans: "Voice Scans", skinScans: "Skin Scans", allTime: "all time",
      flaggedScan: "scan flagged for review", flaggedScans: "scans flagged for review",
      flaggedNote: "These are screening results only — consult a healthcare professional for any concerns.",
      noAnalyses: "No analyses yet", noAnalysesSub: "Run your first voice or skin scan to see results here.",
      voiceAnalysis: "Voice Analysis", skinAnalysis: "Skin Analysis",
      showDetails: "Show details", showLess: "Show less",
      unknown: "Unknown file", justNow: "just now",
      minAgo: "m ago", hrAgo: "h ago",
      voice: "Voice", skin: "Skin", analysis: "Analysis",
      confidence: "Confidence", confirmClear: "Clear all history? This cannot be undone.",
      localNote: "Data is stored only in your browser's localStorage and never sent to a server.",
    },
    disclaimer: {
      title: "Health Screening Tool — Not Medical Advice.",
      body: "WorryLens uses AI to identify patterns and provide general information only. Results are not a medical diagnosis. Always consult a qualified healthcare professional for any health concerns.",
    },
    spinner: { subtext: "This may take a few seconds" },
  },

  es: {
    nav: {
      home: "Inicio", voiceAnalysis: "Análisis de Voz",
      skinAnalysis: "Análisis de Piel", dashboard: "Panel",
    },
    home: {
      badge: "Detección de Salud con IA",
      headline1: "Comprende tu salud",
      headline2: "antes de ir al médico.",
      subtitle: "WorryLens usa IA para analizar tu voz y piel en busca de señales de salud — dándote claridad y contexto, no diagnósticos.",
      analyzeVoice: "Analizar Voz", analyzeSkin: "Analizar Piel",
      whatCanScreen: "¿Qué puede analizar WorryLens?",
      uploadSubtitle: "Sube un archivo o graba directamente — resultados en segundos.",
      voiceBadge: "Grabación en vivo", voiceCardTitle: "Análisis de Voz",
      voiceCardDesc: "Sube un archivo de audio o graba en el navegador. Nuestra IA extrae tono, energía y patrones vocales para identificar signos de estrés, fatiga o problemas respiratorios.",
      skinBadge: "IA Visual", skinCardTitle: "Análisis de Piel",
      skinCardDesc: "Sube una foto de una preocupación cutánea. Nuestra IA identifica condiciones potenciales como acné, erupciones o irregularidades en lunares y hace preguntas de seguimiento.",
      tryItNow: "Pruébalo ahora",
      howItWorks: "Cómo funciona",
      step1Title: "Sube o Graba",   step1Desc: "Arrastra y suelta un archivo o usa tu micrófono/cámara directamente en el navegador. No necesitas cuenta.",
      step2Title: "Análisis con IA", step2Desc: "Nuestro sistema extrae características de audio/visual, aplica detección basada en reglas y genera una explicación clara.",
      step3Title: "Resultados Claros", step3Desc: "Ve tu nivel de riesgo, hallazgos clave y consejos de salud — todo guardado en tu panel personal.",
      privateTitle: "Privado por defecto", privateDesc: "Los archivos se analizan en memoria y nunca se almacenan en nuestros servidores.",
      aiTitle: "Impulsado por WorryLens", aiDesc: "Las explicaciones son generadas desde nuestra base de datos de salud — precisas y matizadas.",
      transparentTitle: "Resultados transparentes", transparentDesc: "Mostramos datos de características en bruto junto con cada explicación de IA.",
    },
    voice: {
      title: "Análisis de Voz", subtitle: "Detecta signos de estrés vocal, fatiga o problemas respiratorios",
      uploadTitle: "Sube o Graba Audio",
      dragDrop: "Arrastra y suelta un archivo de audio", dropHere: "Suelta tu archivo de audio aquí",
      fileTypes: "WAV, MP3, M4A — o haz clic para explorar",
      recordMic: "Grabar desde micrófono", recording: "Grabando",
      stopAnalyze: "Detener y Analizar", analyze: "Analizar",
      loading: "Extrayendo características vocales y generando explicación…",
      errorFallback: "Análisis fallido. ¿Está el backend ejecutándose en el puerto 8001?",
      result: "Resultado", file: "Archivo", confidence: "Confianza",
      anomalyFlags: "Indicadores de anomalía", extractedFeatures: "Características Extraídas",
      pitchMean: "Tono Medio", pitchVariability: "Variabilidad del Tono",
      vocalEnergy: "Energía Vocal", mfccMean: "Media MFCC",
      zeroCrossing: "Tasa de Cruces por Cero", spectralCentroid: "Centroide Espectral",
      aiExplanation: "Explicación de IA", poweredBy: "Impulsado por WorryLens",
      micDenied: "Acceso al micrófono denegado. Permite el acceso al micrófono en la configuración de tu navegador.",
      error: "Error",
    },
    skin: {
      title: "Análisis de Piel", subtitle: "Detección con IA para acné, erupciones, lunares y más",
      uploadTitle: "Sube Foto de Piel",
      dragDrop: "Arrastra y suelta una foto de piel", dropHere: "Suelta tu imagen aquí",
      fileTypes: "JPG, PNG, WEBP — o haz clic para explorar",
      analyzeImage: "Analizar Imagen",
      loading: "WorryLens está pensando…",
      errorFallback: "Análisis fallido. ¿Está el backend ejecutándose en el puerto 8001?",
      riskAssessment: "Evaluación de Riesgo", confidenceLabel: "Confianza",
      possibleConditions: "Posibles Condiciones",
      aiObservations: "Observaciones de IA", poweredByVision: "Imágenes de Base de Datos",
      recommendations: "Recomendaciones",
      followUpTitle: "Preguntas de Seguimiento", followUpSub: "Refina la evaluación",
      followUpPlaceholder: "Tu respuesta…",
      getRefined: "Obtener Evaluación Refinada", refining: "Refinando…",
      refinedAssessment: "Evaluación Refinada", refinedConditions: "Condiciones Refinadas",
      actionPlan: "Plan de Acción", urgency: "Urgencia:",
      followUpFailed: "El análisis de seguimiento falló.", removeImage: "Eliminar imagen",
      error: "Error",
    },
    dashboard: {
      title: "Panel", subtitle: "Tus análisis recientes — almacenados localmente en este navegador",
      clearAll: "Borrar todo", totalScans: "Escaneos Totales",
      voiceScans: "Escaneos de Voz", skinScans: "Escaneos de Piel", allTime: "histórico",
      flaggedScan: "escaneo marcado para revisión", flaggedScans: "escaneos marcados para revisión",
      flaggedNote: "Solo son resultados de detección — consulta a un profesional de salud para cualquier preocupación.",
      noAnalyses: "Sin análisis aún", noAnalysesSub: "Realiza tu primer escaneo de voz o piel para ver los resultados aquí.",
      voiceAnalysis: "Análisis de Voz", skinAnalysis: "Análisis de Piel",
      showDetails: "Mostrar detalles", showLess: "Mostrar menos",
      unknown: "Archivo desconocido", justNow: "ahora mismo",
      minAgo: "min atrás", hrAgo: "h atrás",
      voice: "Voz", skin: "Piel", analysis: "Análisis",
      confidence: "Confianza", confirmClear: "¿Borrar todo el historial? Esto no se puede deshacer.",
      localNote: "Los datos se almacenan solo en el localStorage de tu navegador y nunca se envían a un servidor.",
    },
    disclaimer: {
      title: "Herramienta de Detección de Salud — No es Consejo Médico.",
      body: "WorryLens usa IA para identificar patrones y proporcionar información general únicamente. Los resultados no son un diagnóstico médico. Siempre consulta a un profesional de salud calificado para cualquier preocupación de salud.",
    },
    spinner: { subtext: "Esto puede tomar unos segundos" },
  },

  zh: {
    nav: {
      home: "首页", voiceAnalysis: "语音分析",
      skinAnalysis: "皮肤分析", dashboard: "仪表板",
    },
    home: {
      badge: "AI驱动的健康筛查",
      headline1: "了解您的健康状况",
      headline2: "在就医之前。",
      subtitle: "WorryLens使用AI筛查您的声音和皮肤，寻找潜在健康信号——提供清晰度和背景，而非诊断。",
      analyzeVoice: "分析声音", analyzeSkin: "分析皮肤",
      whatCanScreen: "WorryLens能筛查什么？",
      uploadSubtitle: "上传文件或直接录制——秒级出结果。",
      voiceBadge: "实时录制", voiceCardTitle: "语音分析",
      voiceCardDesc: "上传音频文件或在浏览器中录音。我们的AI提取音调、能量和声音模式，识别紧张、疲劳或可能的呼吸问题迹象。",
      skinBadge: "AI视觉", skinCardTitle: "皮肤分析",
      skinCardDesc: "上传皮肤问题的照片。我们的AI识别潜在病症，如痤疮、皮疹或痣的不规则性，并提出后续问题以进行细化评估。",
      tryItNow: "立即体验",
      howItWorks: "工作原理",
      step1Title: "上传或录制", step1Desc: "拖放文件或直接在浏览器中使用麦克风/摄像头。无需账户。",
      step2Title: "AI分析",     step2Desc: "我们的系统提取音频/视觉特征，运行基于规则的筛查，然后生成通俗易懂的解释。",
      step3Title: "清晰结果",   step3Desc: "查看您的风险级别、关键发现和可操作的健康提示——全部保存到您的个人仪表板。",
      privateTitle: "默认私密",       privateDesc: "文件在内存中分析，从不存储在我们的服务器上。",
      aiTitle: "由WorryLens驱动",     aiDesc: "解释由我们的健康数据库生成——准确且细致。",
      transparentTitle: "透明结果",   transparentDesc: "我们在每次AI解释旁边显示原始特征数据。",
    },
    voice: {
      title: "语音分析", subtitle: "检测声音紧张、疲劳或呼吸问题的迹象",
      uploadTitle: "上传或录制音频",
      dragDrop: "拖放音频文件", dropHere: "将音频文件拖放到这里",
      fileTypes: "WAV、MP3、M4A——或点击浏览",
      recordMic: "从麦克风录制", recording: "录制中",
      stopAnalyze: "停止并分析", analyze: "分析",
      loading: "提取声音特征并生成解释…",
      errorFallback: "分析失败。后端是否在8001端口运行？",
      result: "结果", file: "文件", confidence: "置信度",
      anomalyFlags: "异常标志", extractedFeatures: "提取特征",
      pitchMean: "平均音调", pitchVariability: "音调变异性",
      vocalEnergy: "声音能量", mfccMean: "MFCC均值",
      zeroCrossing: "过零率", spectralCentroid: "频谱质心",
      aiExplanation: "AI解释", poweredBy: "由WorryLens驱动",
      micDenied: "麦克风访问被拒绝。请在浏览器设置中允许麦克风访问。",
      error: "错误",
    },
    skin: {
      title: "皮肤分析", subtitle: "AI驱动的痤疮、皮疹、痣等筛查",
      uploadTitle: "上传皮肤照片",
      dragDrop: "拖放皮肤照片", dropHere: "将图片拖放到这里",
      fileTypes: "JPG、PNG、WEBP——或点击浏览",
      analyzeImage: "分析图片",
      loading: "WorryLens正在思考…",
      errorFallback: "分析失败。后端是否在8001端口运行？",
      riskAssessment: "风险评估", confidenceLabel: "置信度",
      possibleConditions: "可能病症",
      aiObservations: "AI观察", poweredByVision: "由数据库图像驱动",
      recommendations: "建议",
      followUpTitle: "后续问题", followUpSub: "细化评估",
      followUpPlaceholder: "您的回答…",
      getRefined: "获取细化评估", refining: "细化中…",
      refinedAssessment: "细化评估", refinedConditions: "细化病症",
      actionPlan: "行动计划", urgency: "紧迫性：",
      followUpFailed: "后续分析失败。", removeImage: "删除图片",
      error: "错误",
    },
    dashboard: {
      title: "仪表板", subtitle: "您的最近分析——存储在此浏览器本地",
      clearAll: "全部清除", totalScans: "总扫描次数",
      voiceScans: "语音扫描", skinScans: "皮肤扫描", allTime: "历史总计",
      flaggedScan: "次扫描标记待审", flaggedScans: "次扫描标记待审",
      flaggedNote: "这些仅为筛查结果——如有任何顾虑，请咨询医疗专业人员。",
      noAnalyses: "暂无分析", noAnalysesSub: "进行您的第一次语音或皮肤扫描，在此查看结果。",
      voiceAnalysis: "语音分析", skinAnalysis: "皮肤分析",
      showDetails: "显示详情", showLess: "收起",
      unknown: "未知文件", justNow: "刚刚",
      minAgo: "分钟前", hrAgo: "小时前",
      voice: "语音", skin: "皮肤", analysis: "分析",
      confidence: "置信度", confirmClear: "清除所有历史记录？此操作无法撤销。",
      localNote: "数据仅存储在您浏览器的localStorage中，从不发送到服务器。",
    },
    disclaimer: {
      title: "健康筛查工具——非医疗建议。",
      body: "WorryLens使用AI识别模式并仅提供一般信息。结果不是医疗诊断。对于任何健康问题，请始终咨询合格的医疗保健专业人员。",
    },
    spinner: { subtext: "这可能需要几秒钟" },
  },

  fr: {
    nav: {
      home: "Accueil", voiceAnalysis: "Analyse Vocale",
      skinAnalysis: "Analyse Cutanée", dashboard: "Tableau de bord",
    },
    home: {
      badge: "Dépistage de santé par IA",
      headline1: "Comprenez votre santé",
      headline2: "avant la visite chez le médecin.",
      subtitle: "WorryLens utilise l'IA pour analyser votre voix et votre peau à la recherche de signaux de santé — vous offrant clarté et contexte, pas des diagnostics.",
      analyzeVoice: "Analyser la Voix", analyzeSkin: "Analyser la Peau",
      whatCanScreen: "Que peut analyser WorryLens ?",
      uploadSubtitle: "Uploadez un fichier ou enregistrez directement — résultats en secondes.",
      voiceBadge: "Enregistrement en direct", voiceCardTitle: "Analyse Vocale",
      voiceCardDesc: "Uploadez un fichier audio ou enregistrez dans le navigateur. Notre IA extrait la hauteur, l'énergie et les patterns vocaux pour identifier des signes de tension, fatigue ou problèmes respiratoires.",
      skinBadge: "Vision IA", skinCardTitle: "Analyse Cutanée",
      skinCardDesc: "Uploadez une photo d'une préoccupation cutanée. Notre IA identifie les conditions potentielles comme l'acné, les éruptions ou les irrégularités des grains de beauté et pose des questions de suivi.",
      tryItNow: "Essayez maintenant",
      howItWorks: "Comment ça fonctionne",
      step1Title: "Uploadez ou Enregistrez", step1Desc: "Glissez-déposez un fichier ou utilisez votre microphone/caméra directement dans le navigateur. Aucun compte nécessaire.",
      step2Title: "Analyse par IA",          step2Desc: "Notre système extrait les caractéristiques audio/visuelles, effectue un dépistage basé sur des règles, puis génère une explication claire.",
      step3Title: "Résultats Clairs",        step3Desc: "Voyez votre niveau de risque, les découvertes clés et des conseils de santé actionnables — tous sauvegardés dans votre tableau de bord personnel.",
      privateTitle: "Privé par défaut",       privateDesc: "Les fichiers sont analysés en mémoire et ne sont jamais stockés sur nos serveurs.",
      aiTitle: "Propulsé par WorryLens",      aiDesc: "Les explications sont générées depuis notre base de données de santé — précises et nuancées.",
      transparentTitle: "Résultats transparents", transparentDesc: "Nous affichons les données brutes des caractéristiques à côté de chaque explication IA.",
    },
    voice: {
      title: "Analyse Vocale", subtitle: "Détecter les signes de tension vocale, fatigue ou problèmes respiratoires",
      uploadTitle: "Uploader ou Enregistrer l'Audio",
      dragDrop: "Glissez-déposez un fichier audio", dropHere: "Déposez votre fichier audio ici",
      fileTypes: "WAV, MP3, M4A — ou cliquez pour parcourir",
      recordMic: "Enregistrer depuis le microphone", recording: "Enregistrement",
      stopAnalyze: "Arrêter et Analyser", analyze: "Analyser",
      loading: "Extraction des caractéristiques vocales et génération d'explication…",
      errorFallback: "Analyse échouée. Le backend tourne-t-il sur le port 8001 ?",
      result: "Résultat", file: "Fichier", confidence: "Confiance",
      anomalyFlags: "Indicateurs d'anomalie", extractedFeatures: "Caractéristiques Extraites",
      pitchMean: "Hauteur Moyenne", pitchVariability: "Variabilité de la Hauteur",
      vocalEnergy: "Énergie Vocale", mfccMean: "Moyenne MFCC",
      zeroCrossing: "Taux de Passage par Zéro", spectralCentroid: "Centroïde Spectral",
      aiExplanation: "Explication IA", poweredBy: "Propulsé par WorryLens",
      micDenied: "Accès au microphone refusé. Veuillez autoriser l'accès au microphone dans les paramètres de votre navigateur.",
      error: "Erreur",
    },
    skin: {
      title: "Analyse Cutanée", subtitle: "Dépistage par IA pour l'acné, les éruptions, les grains de beauté et plus",
      uploadTitle: "Uploader Photo de Peau",
      dragDrop: "Glissez-déposez une photo de peau", dropHere: "Déposez votre image ici",
      fileTypes: "JPG, PNG, WEBP — ou cliquez pour parcourir",
      analyzeImage: "Analyser l'Image",
      loading: "WorryLens réfléchit…",
      errorFallback: "Analyse échouée. Le backend tourne-t-il sur le port 8001 ?",
      riskAssessment: "Évaluation du Risque", confidenceLabel: "Confiance",
      possibleConditions: "Conditions Possibles",
      aiObservations: "Observations IA", poweredByVision: "Base de données d'images",
      recommendations: "Recommandations",
      followUpTitle: "Questions de Suivi", followUpSub: "Affine l'évaluation",
      followUpPlaceholder: "Votre réponse…",
      getRefined: "Obtenir une Évaluation Affinée", refining: "Affinement…",
      refinedAssessment: "Évaluation Affinée", refinedConditions: "Conditions Affinées",
      actionPlan: "Plan d'Action", urgency: "Urgence :",
      followUpFailed: "L'analyse de suivi a échoué.", removeImage: "Supprimer l'image",
      error: "Erreur",
    },
    dashboard: {
      title: "Tableau de bord", subtitle: "Vos analyses récentes — stockées localement dans ce navigateur",
      clearAll: "Tout effacer", totalScans: "Scans Totaux",
      voiceScans: "Scans Vocaux", skinScans: "Scans Cutanés", allTime: "depuis toujours",
      flaggedScan: "scan signalé pour révision", flaggedScans: "scans signalés pour révision",
      flaggedNote: "Ce sont uniquement des résultats de dépistage — consultez un professionnel de santé pour toute préoccupation.",
      noAnalyses: "Aucune analyse pour l'instant", noAnalysesSub: "Effectuez votre première analyse vocale ou cutanée pour voir les résultats ici.",
      voiceAnalysis: "Analyse Vocale", skinAnalysis: "Analyse Cutanée",
      showDetails: "Afficher les détails", showLess: "Afficher moins",
      unknown: "Fichier inconnu", justNow: "à l'instant",
      minAgo: "min", hrAgo: "h",
      voice: "Voix", skin: "Peau", analysis: "Analyse",
      confidence: "Confiance", confirmClear: "Effacer tout l'historique ? Cette action est irréversible.",
      localNote: "Les données sont stockées uniquement dans le localStorage de votre navigateur et ne sont jamais envoyées à un serveur.",
    },
    disclaimer: {
      title: "Outil de Dépistage de Santé — Pas un Conseil Médical.",
      body: "WorryLens utilise l'IA pour identifier des modèles et fournir des informations générales uniquement. Les résultats ne constituent pas un diagnostic médical. Consultez toujours un professionnel de santé qualifié pour tout problème de santé.",
    },
    spinner: { subtext: "Cela peut prendre quelques secondes" },
  },
};

const LanguageContext = createContext();

export function LanguageProvider({ children }) {
  const [lang, setLang] = useState("en");
  return (
    <LanguageContext.Provider value={{ lang, setLang, t: translations[lang] }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  return useContext(LanguageContext);
}
